/**
 * AddonDetailsSheet - Sheet lateral com detalhes completos do add-on
 * Exibe informações detalhadas, histórico e instâncias vinculadas
 * 
 * @version 1.0
 * @date 2025-12-19
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  ExternalLink,
  Copy,
  ServerCog,
  Calendar,
  CreditCard,
  Activity,
  History,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { TenantAddonWithDetails, AddonAuditLog } from '@/hooks/useAddonManagement';

interface AddonDetailsSheetProps {
  addon: TenantAddonWithDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Interface para instância do provider
 */
interface ProviderInstance {
  id: string;
  status: string;
  instance_name: string | null;
  payment_status: string | null;
  created_at: string;
  activated_at: string | null;
}

/**
 * Componente de status com ícone
 */
function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ElementType }> = {
    active: { variant: 'default', icon: CheckCircle },
    cancelled: { variant: 'destructive', icon: XCircle },
    pending: { variant: 'secondary', icon: Clock },
    expired: { variant: 'outline', icon: AlertCircle },
  };

  const { variant, icon: Icon } = config[status] || { variant: 'outline', icon: AlertCircle };

  return (
    <Badge variant={variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

/**
 * Card de informação
 */
function InfoCard({ label, value, icon: Icon, copyable = false }: {
  label: string;
  value: string | null;
  icon?: React.ElementType;
  copyable?: boolean;
}) {
  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      toast.success('Copiado para a área de transferência');
    }
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
      {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium truncate">{value || '-'}</p>
      </div>
      {copyable && value && (
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleCopy}>
          <Copy className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export function AddonDetailsSheet({ addon, open, onOpenChange }: AddonDetailsSheetProps) {
  // Busca instâncias vinculadas
  const { data: instances, isLoading: isLoadingInstances } = useQuery({
    queryKey: ['addon-instances', addon?.id],
    queryFn: async () => {
      if (!addon) return [];
      
      const { data, error } = await supabase
        .from('provider_instances')
        .select('id, status, instance_name, payment_status, created_at, activated_at')
        .eq('tenant_addon_id', addon.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as ProviderInstance[];
    },
    enabled: !!addon,
  });

  // Busca histórico de auditoria
  const { data: auditHistory, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['addon-audit-history', addon?.id],
    queryFn: async () => {
      if (!addon) return [];
      
      const { data, error } = await supabase
        .from('addon_audit_logs')
        .select('id, action_type, notes, created_at, new_data, old_data')
        .eq('tenant_addon_id', addon.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return (data || []) as AddonAuditLog[];
    },
    enabled: !!addon,
  });

  /**
   * Abre link do Stripe
   */
  const openStripeLink = (subscriptionId: string) => {
    window.open(`https://dashboard.stripe.com/subscriptions/${subscriptionId}`, '_blank');
  };

  if (!addon) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <ServerCog className="h-5 w-5" />
            {addon.plan_addons?.name || 'Add-on'}
          </SheetTitle>
          <SheetDescription className="flex items-center gap-2">
            {addon.tenant?.name}
            <StatusBadge status={addon.status} />
          </SheetDescription>
        </SheetHeader>

        <Separator className="mb-4" />

        <Tabs defaultValue="details" className="flex-1">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Detalhes</TabsTrigger>
            <TabsTrigger value="instances">
              Instâncias
              {instances && instances.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                  {instances.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="history">Histórico</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[calc(100vh-220px)] mt-4">
            {/* Tab de Detalhes */}
            <TabsContent value="details" className="space-y-4 m-0">
              {/* Informações principais */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Informações Gerais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoCard label="ID do Add-on" value={addon.id} copyable />
                  <InfoCard label="Tenant ID" value={addon.tenant_id} copyable />
                  <InfoCard label="Quantidade" value={String(addon.quantity)} />
                  <InfoCard 
                    label="Criado em" 
                    value={format(new Date(addon.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })} 
                    icon={Calendar}
                  />
                  {addon.current_period_start && (
                    <InfoCard 
                      label="Início do Período" 
                      value={format(new Date(addon.current_period_start), 'dd/MM/yyyy', { locale: ptBR })} 
                      icon={Calendar}
                    />
                  )}
                  {addon.current_period_end && (
                    <InfoCard 
                      label="Fim do Período" 
                      value={format(new Date(addon.current_period_end), 'dd/MM/yyyy', { locale: ptBR })} 
                      icon={Calendar}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Informações do Provider */}
              {addon.api_providers && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Provider</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <InfoCard label="Nome" value={addon.api_providers.name} icon={ServerCog} />
                    <InfoCard label="Slug" value={addon.api_providers.slug} copyable />
                  </CardContent>
                </Card>
              )}

              {/* Informações do Stripe */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Stripe
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {addon.stripe_subscription_item_id ? (
                    <>
                      <InfoCard label="Subscription Item ID" value={addon.stripe_subscription_item_id} copyable />
                      {addon.stripe_invoice_id && (
                        <InfoCard label="Invoice ID" value={addon.stripe_invoice_id} copyable />
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Sem integração com Stripe</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab de Instâncias */}
            <TabsContent value="instances" className="space-y-4 m-0">
              {isLoadingInstances ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 animate-pulse" />
                  <p>Carregando instâncias...</p>
                </div>
              ) : instances && instances.length > 0 ? (
                instances.map((instance) => (
                  <Card key={instance.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-mono">
                          {instance.id.slice(0, 8)}...
                        </CardTitle>
                        <StatusBadge status={instance.status} />
                      </div>
                      <CardDescription>
                        {instance.instance_name || 'Sem nome'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-1 text-sm text-muted-foreground">
                      <p>Pagamento: {instance.payment_status || '-'}</p>
                      <p>Criado: {format(new Date(instance.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
                      {instance.activated_at && (
                        <p>Ativado: {format(new Date(instance.activated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ServerCog className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma instância vinculada</p>
                </div>
              )}
            </TabsContent>

            {/* Tab de Histórico */}
            <TabsContent value="history" className="space-y-4 m-0">
              {isLoadingHistory ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 animate-pulse" />
                  <p>Carregando histórico...</p>
                </div>
              ) : auditHistory && auditHistory.length > 0 ? (
                auditHistory.map((log) => (
                  <Card key={log.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Activity className="h-4 w-4" />
                          {log.action_type}
                        </CardTitle>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </span>
                      </div>
                    </CardHeader>
                    {log.notes && (
                      <CardContent className="pt-0">
                        <p className="text-sm text-muted-foreground">{log.notes}</p>
                      </CardContent>
                    )}
                  </Card>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum histórico disponível</p>
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}